import React, { useEffect, useState } from 'react';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { cloneDeep, isEmpty, isNil, uniqBy } from 'lodash';

import { ApiPortfolioState } from '../../dashboard/models/ApiPortfolioState';
import { AssessmentLessonItem } from '../components/AssessmentLessonItem';
import { AssessmentStatus } from '../models/AssessmentStatus';
import { CustomLoadingProgress } from '../../shared/components/CustomLoadingProgress';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Unstable_Grid2';
import InputLabel from '@mui/material/InputLabel';
import LessonPortfolioService from '../services/LessonPortfolioService';
import MenuItem from '@mui/material/MenuItem';
import MultiSelect from '../../shared/components/MultiSelect';
import { PortfolioContextItem } from '../models/Portfolio';
import SearchTextField from '../../shared/components/SearchTextField';
import Typography from '@mui/material/Typography';
import User from '../../membership/models/User';
import styles from '../styles/AssessmentLessonList.module.css';
import { useAssessments } from '../hooks/useAssessments';
import { useNavigate } from 'react-router-dom';

const statusFilterOptions: { label: string; value: AssessmentStatus }[] = [
  {
    label: 'SUBMITTED', //Learner has submitted the portfolio
    value: 'submitted'
  },
  {
    label: 'MARKING STARTED',
    value: 'markingStarted'
  },
  {
    label: 'WORK REQUESTED',
    value: 'workRequested'
  }
];

interface Assessors {
  label: string;
  value: number;
}

interface AssessmentProps {
  user: User;
  isFinalised?: boolean;
}

export function AssessmentLessonList(props: AssessmentProps) {
  const navigate = useNavigate();

  const [selectedCourse, setSelectedCourse] = useState<number>(0);
  const [assessmentPortfolios, setAssessmentPortfolios] = useState<
    PortfolioContextItem[]
  >([]);
  const [allCourses, setAllCourses] = useState([]);
  const [selectedPortfolio, setSelectedPortfolio] =
    useState<PortfolioContextItem>(null);
  const [assessorsOptions, setAssessorsOptions] = useState<Assessors[]>([]);
  const [searchText, setSearchText] = useState('');
  const [filterByStatus, setFilterByStatus] = useState<string[]>([]);
  const [filterByMarker, setFilterByMarker] = useState<number[]>([]);

  const isMasterMarker = LessonPortfolioService.isMasterMarker();

  const {
    listCoursesLoading,
    lessonsLoading,
    listSubmittedPortfolios,
    portfoliosLoading,
    portfoliosError,
    submittedPortfolios,
    assignLoading,
    assignMarker,
    unassignLoading,
    unassignMarker
  } = useAssessments();

  useEffect(() => {
    listSubmittedPortfolios();
  }, []);

  useEffect(() => {
    if (submittedPortfolios) {
      //Filter portfolios by finalized or not and set that as state
      const current = filterPortfolios();
      setAssessmentPortfolios(current);

      //Get unique courses information from portfolios
      if (selectedCourse && selectedCourse != 0) {
        filterByCourse(selectedCourse);
      }
      const uniqueCourses = uniqBy(submittedPortfolios, 'coursekey').map(
        ({ coursekey, course }) => ({ coursekey, course })
      );
      setAllCourses(uniqueCourses);

      //Get marker information from portfolios
      const markers = submittedPortfolios
        ?.filter((p) => !isNil(p.marker?.userkey)) //filter undefined/null marker
        .map((p) => p.marker);

      //Get unique marker details
      const assessorFilterOptions: Assessors[] = uniqBy(
        markers,
        'userkey'
      )?.map((m) => {
        return {
          label:
            props.user?.userkey === m.userkey
              ? 'ME'
              : `${m.firstname} ${m.lastname}`,
          value: m.userkey
        };
      });

      //Add 'Unassigned' to markers/assessors list
      const initial: Assessors = { label: 'Unassigned', value: null }; //value should be null because marker's userkey will be null for all unassigned portfolios
      if (!props.isFinalised) {
        assessorFilterOptions.unshift({ ...initial }); //Add unassigned to existing markers
      }
      setAssessorsOptions(assessorFilterOptions);

      //Empty filters when changing the tabs(open/finalized assessments)
      setFilterByStatus([]);
    }
  }, [submittedPortfolios, props.isFinalised]);

  const filterPortfolios = () => {
    if (props.isFinalised) {
      const finalized = submittedPortfolios.filter(
        (p) => p.state === ApiPortfolioState.AssessmentLock
      );
      return finalized;
    } else {
      const allSubmitted = submittedPortfolios.filter(
        (p) => p.state !== ApiPortfolioState.AssessmentLock
      );
      return allSubmitted;
    }
  };

  function handleCourseChange(event: SelectChangeEvent<number>) {
    const coursekey = +event.target.value;
    setSelectedCourse(coursekey);
    filterByCourse(coursekey);
  }

  function handleSearch(value: string) {
    setSearchText(value);
  }

  function handleFilter(filters: string[]) {
    setFilterByStatus(filters);
  }

  function handleFilterMarker(filters: number[]) {
    setFilterByMarker(filters);
  }

  const filterByCourse = (coursekey: number) => {
    const current = filterPortfolios();
    let copy = cloneDeep(current);
    if (coursekey !== 0) {
      copy = copy?.filter((p) => p.coursekey === coursekey);
    }
    setAssessmentPortfolios(copy);
  };

  let filteredAssessments = LessonPortfolioService.filterByStatus(
    LessonPortfolioService.filterPortfoliosBy(assessmentPortfolios, searchText),
    filterByStatus
  );

  if (isMasterMarker) {
    filteredAssessments = LessonPortfolioService.filterByMarker(
      filteredAssessments,
      filterByMarker
    );
  }

  function handleSelect(portfolio: PortfolioContextItem) {
    const { userkey, modulekey, moduleversion, layer } = portfolio;
    navigate(
      `/assessments/view?coursekey=${selectedCourse}&userkey=${userkey}&modulekey=${modulekey}&moduleversion=${moduleversion}&layer=${layer}`
    );
  }

  function handleAssessor(portfolio: PortfolioContextItem, memberkey: number) {
    setSelectedPortfolio(portfolio);

    const { userkey, modulekey, layer } = portfolio;
    if (memberkey === 0) {
      unassignMarker(userkey, modulekey, layer);
    } else {
      assignMarker(userkey, modulekey, layer, memberkey);
    }
  }

  return (
    <Grid container alignItems="center" direction="column">
      <Grid container xs={12} md={8} spacing={1}>
        <Grid xs={12} md={isMasterMarker && !props.isFinalised ? 3 : 4}>
          <FormControl
            className={styles.formControl}
            variant="outlined"
            margin="dense"
            size="small"
          >
            <InputLabel id="courseId-label">Choose a course</InputLabel>
            <Select
              labelId="courseId-label"
              label="Choose a course"
              id="course-id"
              value={isEmpty(allCourses) ? -1 : selectedCourse}
              disabled={isEmpty(allCourses)}
              onChange={handleCourseChange}
            >
              <MenuItem value={0}>All courses</MenuItem>
              {isEmpty(allCourses) ? (
                <MenuItem value={-1}>No courses available</MenuItem>
              ) : (
                allCourses?.map((course) => (
                  <MenuItem key={course.coursekey} value={course.coursekey}>
                    {course.course}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>
        <Grid xs={12} md={isMasterMarker && !props.isFinalised ? 3 : 4}>
          <SearchTextField
            style={{ width: '100%', marginLeft: 'unset' }}
            trigger="type"
            disabled={isEmpty(allCourses)}
            onSearch={handleSearch}
          />
        </Grid>
        {!props.isFinalised && (
          <Grid xs={12} md={isMasterMarker ? 3 : 4}>
            <MultiSelect<string>
              items={statusFilterOptions}
              width={'100%'}
              onChange={handleFilter}
              label="Filter by status"
              disabled={isEmpty(allCourses)}
            />
          </Grid>
        )}
        {isMasterMarker && (
          <Grid xs={12} md={!props.isFinalised ? 3 : 4}>
            <MultiSelect<number>
              items={assessorsOptions}
              width={'100%'}
              onChange={handleFilterMarker}
              label="Filter by marker"
              disabled={isEmpty(allCourses)}
            />
          </Grid>
        )}
      </Grid>
      <Grid
        container
        xs={12}
        md={8}
        className={styles.assesmentItem}
        justifyContent="center"
      >
        {lessonsLoading || listCoursesLoading || portfoliosLoading ? (
          <div className={styles.root}>
            <CustomLoadingProgress />
          </div>
        ) : isEmpty(assessmentPortfolios) ? (
          <Typography variant="body1" align="center">
            There aren't any submitted lessons from this group yet.
          </Typography>
        ) : (
          filteredAssessments?.map((portfolio) => {
            const { userkey, modulekey, layer } = portfolio;
            return (
              <AssessmentLessonItem
                key={`${userkey}-${modulekey}-${layer}`}
                assignLoading={
                  userkey === selectedPortfolio?.userkey &&
                  modulekey === selectedPortfolio?.modulekey &&
                  layer === selectedPortfolio?.layer &&
                  (assignLoading || unassignLoading)
                }
                assessmentPortfolio={portfolio}
                userkey={props.user?.userkey}
                isFinalised={props.isFinalised}
                onSelect={handleSelect}
                onAssessorChange={handleAssessor}
              />
            );
          })
        )}
      </Grid>
    </Grid>
  );
}
